import React, { useState } from 'react';
import { checkInActivity } from '../utils/api';

const SLOT_LABELS = { 1: ['Daily'], 2: ['Morning', 'Evening'], 3: ['Morning', 'Afternoon', 'Evening'] };
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDateStr(d) {
  var dt = new Date(d);
  return dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
}

function getScheduledDates(act) {
  var start = new Date(act.startDate); start.setHours(0, 0, 0, 0);
  var end = act.endDate ? new Date(act.endDate) : new Date(); end.setHours(23, 59, 59, 999);
  var today = new Date();
  if (end > today) end = today;
  var dates = [];
  var cur = new Date(start);
  while (cur <= end) {
    var dow = cur.getDay();
    var scheduled = act.frequency === 'daily' || act.daysOfWeek.length === 0 || act.daysOfWeek.includes(dow);
    if (scheduled) dates.push(toDateStr(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function compliance(act) {
  var scheduled = getScheduledDates(act);
  if (scheduled.length === 0) return 100;
  var expected = scheduled.length * act.timesPerDay;
  var done = act.checkIns ? act.checkIns.filter(function(c) { return scheduled.includes(c.date); }).length : 0;
  return Math.round((done / expected) * 100);
}

function getSlotStates(checkIns, dateStr, slots) {
  return slots.map(function(label, idx) {
    var done = false;
    if (checkIns) {
      for (var j = 0; j < checkIns.length; j++) {
        if (checkIns[j].date === dateStr && checkIns[j].slotIndex === idx) { done = true; break; }
      }
    }
    return { label: label, idx: idx, done: done };
  });
}

// Last 14 days with slot-level detail
function buildHistory(act, days) {
  var result = [];
  for (let i = days - 1; i >= 0; i--) {
    let d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
    let ds = toDateStr(d);
    let dow = d.getDay();
    let scheduled = act.frequency === 'daily' || act.daysOfWeek.length === 0 || act.daysOfWeek.includes(dow);
    if (!scheduled || new Date(act.startDate) > d) continue;
    let slots = SLOT_LABELS[act.timesPerDay] || ['Daily'];
    let slotStates = getSlotStates(act.checkIns, ds, slots);
    result.push({ date: ds, dayLabel: DAY_NAMES[d.getDay()] + ' ' + d.getDate(), slotStates, isPast: i > 0 });
  }
  return result;
}

const PatientActivityCheckin = ({ requestId, activities = [], onUpdate }) => {
  const [checkingIn, setCheckingIn] = useState(null); // { actId, date, slotIndex }
  const [showHistory, setShowHistory] = useState(null); // actId

  const active = activities.filter(function(a) { return a.isActive; });

  async function handleCheckIn(actId, date, slotIndex) {
    var key = actId + '-' + date + '-' + slotIndex;
    setCheckingIn(key);
    try {
      var res = await checkInActivity(requestId, actId, { date, slotIndex });
      // Merge updated checkIns back into the activities list
      var updated = activities.map(function(a) {
        if (a._id === actId) return { ...a, checkIns: res.data.checkIns };
        return a;
      });
      onUpdate(updated);
    } catch (e) {
      if (e.response && e.response.status === 409) alert('Already checked in for this slot.');
      else alert('Could not save check-in. Try again.');
    }
    setCheckingIn(null);
  }

  const s = {
    actCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '16px', marginBottom: '14px' },
    actName: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '16px', color: '#1a1a2e', marginBottom: '4px' },
    actDesc: { fontSize: '13px', color: '#666', marginBottom: '12px', lineHeight: '1.6' },
    progressBar: { height: '8px', borderRadius: '4px', background: '#f0f0f0', overflow: 'hidden', marginBottom: '6px' },
    pctLabel: { fontSize: '12px', color: '#666', marginBottom: '14px' },
    sectionLabel: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: '#888', fontFamily: "'Raleway', sans-serif", marginBottom: '10px' },
    todayRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' },
    slotBtn: function(done, loading) {
      return {
        flex: 1, minWidth: '100px', padding: '14px 10px', borderRadius: '8px',
        border: done ? '2px solid #27ae60' : '2px solid #e0e0e0',
        background: done ? '#eafaf1' : loading ? '#f9faf7' : '#fff',
        color: done ? '#27ae60' : '#333',
        fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '13px',
        cursor: done || loading ? 'default' : 'pointer',
        textAlign: 'center', transition: 'all 0.2s'
      };
    },
    slotIcon: { fontSize: '20px', display: 'block', marginBottom: '4px' },
    histBtn: { background: 'transparent', border: '1px solid #e0e0e0', color: '#999', borderRadius: '4px', padding: '6px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
    histGrid: { marginTop: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' },
    histRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', fontSize: '13px' },
    histDate: { width: '70px', fontSize: '11px', color: '#999', flexShrink: 0 },
    histSlots: { display: 'flex', gap: '6px' },
    histSlotDot: function(done) { return { width: '28px', height: '28px', borderRadius: '6px', background: done ? '#eafaf1' : '#fdecea', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', border: '1px solid ' + (done ? '#27ae60' : '#f5c6c6') }; }
  };

  if (active.length === 0) {
    return (
      <div style={{ background: '#f9faf7', border: '1px dashed #ccc', borderRadius: '8px', padding: '24px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
        No activities assigned yet. Your healer will assign activities as part of your healing programme.
      </div>
    );
  }

  return (
    <div>
      {active.map(function(act) {
        var p = compliance(act);
        var slots = SLOT_LABELS[act.timesPerDay] || ['Daily'];
        var today = toDateStr(new Date());
        var slotIcons = { 'Daily': '🌿', 'Morning': '🌅', 'Afternoon': '☀️', 'Evening': '🌙' };

        // Today's slot states
        var todaySlots = slots.map(function(label, idx) {
          var done = act.checkIns && act.checkIns.some(function(c) { return c.date === today && c.slotIndex === idx; });
          return { label, idx, done };
        });

        // History (last 14 scheduled days)
        var history = buildHistory(act, 30).slice(-14);

        var progressColor = p >= 70 ? '#27ae60' : p >= 40 ? '#f39c12' : '#e74c3c';
        var showing = showHistory === act._id;

        return (
          <div key={act._id} style={s.actCard}>
            <div style={s.actName}>{act.name}</div>
            {act.description && <div style={s.actDesc}>{act.description}</div>}

            {/* Progress bar */}
            <div style={s.progressBar}>
              <div style={{ height: '100%', width: p + '%', background: progressColor, borderRadius: '4px', transition: 'width 0.5s ease' }} />
            </div>
            <div style={s.pctLabel}>{p}% overall compliance</div>

            {/* Today's check-in */}
            <div style={s.sectionLabel}>Today's Check-in</div>
            <div style={s.todayRow}>
              {todaySlots.map(function(slot) {
                var key = act._id + '-' + today + '-' + slot.idx;
                var loading = checkingIn === key;
                return (
                  <button key={slot.idx} disabled={slot.done || !!loading}
                    onClick={function() { handleCheckIn(act._id, today, slot.idx); }}
                    style={s.slotBtn(slot.done, loading)}>
                    <span style={s.slotIcon}>{slot.done ? '✅' : loading ? '⏳' : (slotIcons[slot.label] || '🌿')}</span>
                    {slot.label}
                    {slot.done && <div style={{ fontSize: '10px', color: '#27ae60', marginTop: '2px' }}>Done</div>}
                  </button>
                );
              })}
            </div>

            {/* History toggle */}
            <button onClick={function() { setShowHistory(showing ? null : act._id); }} style={s.histBtn}>
              {showing ? 'Hide History' : 'View History (' + (act.checkIns ? act.checkIns.length : 0) + ' check-ins)'}
            </button>

            {showing && (
              <div style={s.histGrid}>
                <div style={s.sectionLabel}>Last 14 Days</div>
                {history.length === 0 && <div style={{ fontSize: '12px', color: '#999' }}>No scheduled days in this window yet.</div>}
                {history.map(function(day) {
                  return (
                    <div key={day.date} style={s.histRow}>
                      <div style={s.histDate}>{day.dayLabel}</div>
                      <div style={s.histSlots}>
                        {day.slotStates.map(function(slot) {
                          var canLateCheckIn = !slot.done && day.isPast;
                          var key = act._id + '-' + day.date + '-' + slot.idx;
                          var loading = checkingIn === key;
                          return (
                            <div key={slot.idx} title={slot.label + (slot.done ? ' — done' : canLateCheckIn ? ' — tap to mark (late)' : '')}
                              onClick={canLateCheckIn && !loading ? function() { handleCheckIn(act._id, day.date, slot.idx); } : undefined}
                              style={{ ...s.histSlotDot(slot.done), cursor: canLateCheckIn ? 'pointer' : 'default', opacity: loading ? 0.5 : 1 }}>
                              {slot.done ? '✓' : loading ? '…' : canLateCheckIn ? '+' : '✗'}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        {day.slotStates.filter(function(s) { return s.done; }).length}/{day.slotStates.length} slots
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PatientActivityCheckin;
